import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { getLevel } from "@/lib/audio";

const LABELS = { idle: "En veille", listening: "J'écoute…", thinking: "Je réfléchis…", speaking: "Sem.IA parle" };

export default function Avatar({ state = "idle", analyser, className = "w-[200px]" }) {
  const mouth = useRef(null);
  const inner = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let raf;
    let smooth = 0;
    const tick = () => {
      const lvl = stateRef.current === "speaking" ? Math.min(getLevel(analyser?.current) * 5, 1) : 0;
      smooth += (lvl - smooth) * 0.35;
      mouth.current?.setAttribute("ry", (1.5 + smooth * 11).toFixed(2));
      mouth.current?.setAttribute("rx", (11 - smooth * 2.5).toFixed(2));
      inner.current?.setAttribute("ry", Math.max(0, smooth * 8 - 1).toFixed(2));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [analyser]);

  const active = state !== "idle";
  return (
    <div data-testid="semia-avatar" className={`relative flex flex-col items-center ${className}`}>
      <motion.div
        className="absolute rounded-full bg-[var(--accent)] blur-3xl w-[90%] aspect-square top-[5%]"
        animate={{ opacity: active ? [0.25, 0.45, 0.25] : 0.18, scale: state === "listening" ? [1, 1.12, 1] : 1 }}
        transition={{ duration: 1.4, repeat: Infinity }}
      />
      <motion.svg
        viewBox="0 0 200 200" className="relative w-full h-auto"
        animate={{ rotate: state === "speaking" ? [-2, 2, -2] : state === "thinking" ? [0, -4, 0] : 0, y: [0, -3, 0] }}
        transition={{ duration: state === "speaking" ? 2.4 : 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <defs>
          <radialGradient id="skin" cx="45%" cy="40%" r="65%">
            <stop offset="0%" stopColor="#FFE1CF" />
            <stop offset="100%" stopColor="#F1B79A" />
          </radialGradient>
          <linearGradient id="hair" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3A1F18" />
            <stop offset="100%" stopColor="#1E0F0B" />
          </linearGradient>
        </defs>
        <path d="M40 104 C34 48 72 26 102 26 C138 26 170 50 162 110 C160 140 166 168 172 196 L28 196 C34 168 42 140 40 104Z" fill="url(#hair)" />
        <rect x="86" y="150" width="28" height="30" rx="10" fill="#EBAA8B" />
        <path d="M44 200 C48 176 72 168 100 168 C128 168 152 176 156 200Z" fill="var(--accent)" />
        <ellipse cx="100" cy="102" rx="46" ry="56" fill="url(#skin)" />
        <path d="M52 92 C50 58 76 38 106 40 C136 42 152 62 150 94 C144 76 132 64 116 60 C104 72 80 78 52 92Z" fill="url(#hair)" />
        <ellipse cx="70" cy="128" rx="10" ry="6" fill="#F28B78" opacity=".45" />
        <ellipse cx="130" cy="128" rx="10" ry="6" fill="#F28B78" opacity=".45" />
        <g className="avatar-eyes">
          <ellipse cx="78" cy="104" rx="6" ry="7.5" fill="#1E0F0B" />
          <ellipse cx="122" cy="104" rx="6" ry="7.5" fill="#1E0F0B" />
          <circle cx="80" cy="101" r="2" fill="#fff" />
          <circle cx="124" cy="101" r="2" fill="#fff" />
        </g>
        <path d="M66 90 Q78 84 89 89" stroke="#2A1510" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M111 89 Q122 84 134 90" stroke="#2A1510" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M100 110 Q96 122 101 124" stroke="#D99478" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <ellipse ref={mouth} cx="100" cy="142" rx="11" ry="1.5" fill="#B5332A" />
        <ellipse ref={inner} cx="100" cy="144" rx="6" ry="0" fill="#5E1410" />
      </motion.svg>
      <span data-testid="voice-orb-state" className="label-mono text-[var(--text-muted)] mt-1">{LABELS[state]}</span>
    </div>
  );
}
